import React from 'react';
import _get from 'lodash/get';
import _map from 'lodash/map';
import PropTypes from 'prop-types';
// Material UI
import {
  darken, lighten, makeStyles,
} from '@material-ui/core/styles';
import MuiTableRow from '@material-ui/core/TableRow';
// Core components
import TableCell from 'components/core/table/TableCell';
// Custom
import { listRowSpecsShape } from 'constants/PropTypesShapes';
import { CollapseRowIconButton, ChooseIconButton } from 'components/buttons/IconButtons';

const useStyles = (withChoose, background) => makeStyles((theme) => {
  let root = {};
  if (background === 'default') {
    root = {
      ...root,
      backgroundColor: theme.palette.background.default,
    };
  } else if (background === 'defaultDarken') {
    root = {
      ...root,
      backgroundColor: darken(theme.palette.background.default, 0.05),
    };
  } else if (background === 'primary') {
    root = {
      ...root,
      backgroundColor: lighten(theme.palette.primary.light, 0.85),
    };
  }
  let hover = {};
  if (withChoose) {
    hover = {
      ...hover,
      '&:hover': {
        cursor: 'pointer',
      },
    };
  }
  return { root, hover };
});

const TableRow = React.forwardRef(({
  rowId, element, hover, hidden, rowSpecs, background, actions, children, style,
}, ref) => {
  // Row specs
  const {
    withSelection, selected, withChoose, chooseTooltipId,
    withActions, nbActions, withDetails, showDetails,
    onShowHideDetails, onClick, onDoubleClick,
  } = rowSpecs || {};

  // Expanded flag
  const expanded = withDetails && showDetails;

  // Style
  const classes = useStyles(withChoose, background || (expanded ? 'defaultDarken' : null))();

  // Handlers
  const handleClick = () => {
    onClick(element);
  };

  const handleDoubleClick = () => {
    onDoubleClick(element);
  };

  const handleShowHideDetails = () => {
    onShowHideDetails(_get(element, 'id'));
  };

  // Component props
  let tableRowProps = {
    id: rowId,
    selected,
    hover,
  };
  tableRowProps = {
    ...tableRowProps,
    style: {
      ...(style || {}),
      ...(hidden ? { display: 'none' } : {}),
    },
  };
  if (onClick) {
    tableRowProps = {
      ...tableRowProps,
      onClick: handleClick,
    };
  }
  if (onDoubleClick) {
    tableRowProps = {
      ...tableRowProps,
      onDoubleClick: handleDoubleClick,
    };
  }

  return (
    <MuiTableRow ref={ref} {...tableRowProps} classes={classes}>
      <React.Fragment>
        {element && withDetails && (
          <TableCell className="table-details" noPadding expanded={expanded}>
            <CollapseRowIconButton
              expanded={showDetails}
              onClick={handleShowHideDetails}
            />
          </TableCell>
        )}
        {element && withSelection && (
          <TableCell role="checkbox" selected={selected} expanded={expanded} />
        )}
        {_map(React.Children.toArray(children), (child) => React.cloneElement(child, { expanded }))}
        {withActions && (
          <TableCell className={`table-actions-${nbActions}`} noPadding expanded={expanded}>
            <div className="d-flex justify-content-end align-items-center">
              {actions}
            </div>
          </TableCell>
        )}
        {withChoose && (
          <TableCell className="table-actions-1" noPadding expanded={expanded}>
            <div className="d-flex justify-content-end">
              <ChooseIconButton key="bChoose" tooltipId={chooseTooltipId} />
            </div>
          </TableCell>
        )}
      </React.Fragment>
    </MuiTableRow>
  );
});

TableRow.displayName = 'TableRow';

TableRow.propTypes = {
  rowId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  element: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }),
  hover: PropTypes.bool,
  hidden: PropTypes.bool,
  rowSpecs: listRowSpecsShape,
  background: PropTypes.oneOf(['default', 'primary']),
  actions: PropTypes.arrayOf(PropTypes.node),
  children: PropTypes.node.isRequired,
  style: PropTypes.object,
};

TableRow.defaultProps = {
  rowId: null,
  element: null,
  hover: true,
  hidden: false,
  rowSpecs: null,
  background: null,
  actions: null,
  style: null,
};

export default TableRow;



// WEBPACK FOOTER //
// ./components/core/table/TableRow.js